import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import { UInput } from '@/core/components/ui'
import { useTranslate } from '@repo/unicity'
import { useEffect, useMemo, useState } from 'react'
import ExpDate from './ExpDate'
import { creditCardState } from '@/core/store/creditcard.store'
import { useSetAtom } from 'jotai'
import cardValidator from 'card-validator'
import { isSupportedCardBrand } from '@/core/constants/payment'

// Card type icons
import VisaIcon from './assets/visa.svg?react'
import MastercardIcon from './assets/mastercard.svg?react'
import AmericanExpressIcon from './assets/american-express.svg?react'
import DiscoverIcon from './assets/discover.svg?react'

// Map card type to icon component
const CARD_TYPE_ICONS: Record<string, React.FC> = {
    visa: VisaIcon,
    mastercard: MastercardIcon,
    'american-express': AmericanExpressIcon,
    discover: DiscoverIcon
}

const CreditCardAdd = () => {
    const { translate } = useTranslate()
    const setCreditCardState = useSetAtom(creditCardState)
    const [nameOnCard, setNameOnCard] = useState('')
    const [cardNumber, setCardNumber] = useState('')
    const [, setExpDate] = useState('')
    const [cvv, setCvv] = useState('')

    // Detect card type from card number
    const cardType = useMemo(() => {
        const cleanNumber = cardNumber.replace(/\s/g, '')
        if (!cleanNumber) return null

        const validation = cardValidator.number(cleanNumber)
        return validation.card?.type || null
    }, [cardNumber])

    // Keep detected brand in store so payment submission can gate on it
    useEffect(() => {
        setCreditCardState(prev => ({ ...prev, cardType }))
    }, [cardType, setCreditCardState])

    // Brand is unsupported once card-validator can confidently identify it.
    // While the number is still ambiguous, cardType is null → no error shown.
    const isUnsupportedBrand = cardType !== null && !isSupportedCardBrand(cardType)
    const unsupportedMsg = translate('accepts_visa_mastercard')
    const unsupportedText = unsupportedMsg === 'accepts_visa_mastercard' ? 'This site accepts Visa and Mastercard.' : unsupportedMsg

    // Get card icon component based on detected card type
    const CardIcon = cardType ? CARD_TYPE_ICONS[cardType] : null

    const formatCardNumber = (value: string) => {
        if (value.length === 0) {
            setCardNumber('')
            setCreditCardState(prev => ({ ...prev, cardNumber: '' }))
            return
        }
        if (!/^\d+$/.test(value)) return value
        if (value.length > 16) return value

        const cleanValue = value.replace(/\s/g, '')
        setCardNumber(cleanValue.replace(/(\d{4})(?=\d)/g, '$1 '))
        setCreditCardState(prev => ({ ...prev, cardNumber: cleanValue }))
    }

    const formatCvv = (value: string) => {
        if (value.length === 0) {
            setCvv('')
            setCreditCardState(prev => ({ ...prev, cvv: '' }))
            return
        }
        if (!/^\d+$/.test(value)) return
        if (value.length > 4) return

        setCvv(value)
        setCreditCardState(prev => ({ ...prev, cvv: value }))
    }

    return (
        <Stack gap={'24px'} width={'100%'}>
            <Stack
                rowGap={'24px'}
                columnGap={'12px'}
                display={'grid'}
                gridTemplateColumns={{
                    xs: '1fr',
                    md: '1fr'
                }}>
                <UInput
                    label={translate('name_on_card')}
                    value={nameOnCard}
                    onChange={e => {
                        setNameOnCard(e.target.value)
                        setCreditCardState(prev => ({ ...prev, nameOnCard: e.target.value }))
                    }}
                    onClear={() => {
                        setNameOnCard('')
                        setCreditCardState(prev => ({ ...prev, nameOnCard: '' }))
                    }}
                    sx={{
                        '&.MuiFormControl-root .MuiInputLabel-root': {
                            color: 'rgba(98, 116, 142, 1)!important'
                        }
                    }}
                />
                <UInput
                    type="tel"
                    label={translate('card_number')}
                    value={cardNumber}
                    onChange={e => formatCardNumber(e.target.value.replace(/\s/g, ''))}
                    error={isUnsupportedBrand}
                    slotProps={{
                        input: {
                            endAdornment: CardIcon && (
                                <InputAdornment position="end" sx={{ svg: { width: '30px', height: '20px' }, marginLeft: 0 }}>
                                    <CardIcon />
                                </InputAdornment>
                            )
                        }
                    }}
                    sx={{
                        '&.MuiFormControl-root .MuiInputLabel-root': {
                            color: 'rgba(98, 116, 142, 1)!important'
                        }
                    }}
                />
                {isUnsupportedBrand && (
                    <Box
                        sx={{
                            mt: '-12px',
                            p: '12px 16px',
                            borderRadius: '8px',
                            bgcolor: 'rgba(255, 231, 235, 1)'
                        }}>
                        <Typography
                            sx={{
                                fontFamily: 'var(--font-inter)',
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '100%',
                                letterSpacing: 0,
                                textAlign: 'center',
                                color: '#FE5D7C'
                            }}>
                            {unsupportedText}
                        </Typography>
                    </Box>
                )}
                <ExpDate
                    onChange={e => {
                        setExpDate(e)
                        setCreditCardState(prev => ({ ...prev, expDate: e }))
                    }}
                />
                <UInput
                    type="tel"
                    label={translate('cvv')}
                    value={cvv}
                    onChange={e => formatCvv(e.target.value)}
                    onClear={() => {
                        setCvv('')
                        setCreditCardState(prev => ({ ...prev, cvv: '' }))
                    }}
                    sx={{
                        '&.MuiFormControl-root .MuiInputLabel-root': {
                            color: 'rgba(98, 116, 142, 1)!important'
                        },
                        '& .MuiInputBase-input': {
                            WebkitTextSecurity: 'disc'
                        }
                    }}
                />
            </Stack>
        </Stack>
    )
}

export default CreditCardAdd
